import { useState, useCallback } from 'react';
import {
  Order_list,
  Search_Store_Orders,
  Search_Store_Orders_Suggestions,
  Sort_Order_list,
  getOrderDetails,
  fetchOrderDetailsStatus,
  fetchSellerCancellationReasons,
  sellerCancelLineItem,
} from '../../Api';
import { 
  Order, 
  SearchPayload, 
  OrderListResponse, 
  SearchOrderResponse, 
  OrderDetailsResponse,
  DropdownOption,
  Suggestion 
} from './types';

export const useOrderList = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pages, setPages] = useState<number>(0);

  const fetchOrders = useCallback(async (userToken: string | null, currentPage: number) => {
    try {
      setLoading(true);
      const response = await Order_list(userToken, currentPage);
      if (response?.status === 200) {
        setOrders(response.data.order_list);
        setPages(response.data.count);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching Order details", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { orders, loading, pages, fetchOrders, setOrders, setPages };
};

export const useOrderSearch = () => {
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const searchOrders = useCallback(async (payload: SearchPayload, currentPage: number) => {
    try {
      setSearchLoading(true);
      const response = await Search_Store_Orders(payload, currentPage);
      if (response?.status === 200) {
        return {
          orders: response.data.user_order_list,
          count: response.data.count
        };
      }
      return { orders: [], count: 0 };
    } catch (error) {
      console.error("Error searching orders", error);
      throw error;
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const fetchSuggestions = useCallback(async (payload: SearchPayload) => {
    try {
      const response = await Search_Store_Orders_Suggestions(payload);
      if (response?.status === 200) {
        const formattedSuggestions = response.data.order_id_suggestions.map(
          (order: any) => ({
            id: order.id,
            name: `Order: ${order.id}`,
          })
        );
        setSuggestions(formattedSuggestions);
        return formattedSuggestions;
      }
      return [];
    } catch (error) {
      console.error("Error fetching suggestions", error);
      setSuggestions([]);
      return [];
    }
  }, []);

  return { 
    searchLoading, 
    suggestions, 
    searchOrders, 
    fetchSuggestions,
    setSuggestions 
  };
};

export const useOrderSort = () => {
  const [sortLoading, setSortLoading] = useState<boolean>(false);

  const sortOrders = useCallback(async (sortType: string) => {
    try {
      setSortLoading(true);
      const response = await Sort_Order_list(sortType);
      if (response?.status === 200) {
        return {
          orders: response.data.order_list,
          count: response.data.count
        };
      }
      return { orders: [], count: 0 };
    } catch (error) {
      console.error("Error sorting orders", error);
      throw error;
    } finally {
      setSortLoading(false);
    }
  }, []);

  return { sortLoading, sortOrders };
};

export const useOrderDetails = () => {
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [productIds, setProductIds] = useState<number[]>([]);

  const fetchOrderDetails = useCallback(async (orderId: string) => {
    try {
      setDetailsLoading(true);
      const response = await getOrderDetails(orderId);
      
      const { product_list } = response || {};
      if (product_list?.length > 0) {
        setOrderDetails(response);
        
        const ids: (number | undefined)[] = product_list.map(
          (product: { product: { id: number } }) => product.product.id
        );
        const validIds = ids.filter((id): id is number => id !== undefined);
        setProductIds(validIds);
        
        return response;
      } else {
        throw new Error("No products found in the order details.");
      }
    } catch (error: any) {
      console.error("Error fetching Order Details:", error?.message || error);
      throw error;
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  return { 
    orderDetails, 
    detailsLoading, 
    productIds, 
    fetchOrderDetails,
    setProductIds 
  };
};

export const useOrderStatus = () => {
  const [statusList, setStatusList] = useState<DropdownOption[]>([]);
  const [reasonOptions, setReasonOptions] = useState<string[]>([]);

  const fetchStatusList = useCallback(async (currentStatus: string, deliveryMethod: string) => {
    try {
      const response = await fetchOrderDetailsStatus();
      
      if (response?.status && response?.status_list && Object.keys(response.status_list).length > 0) {
        const mappedStatusKey = mapOrderStatus(currentStatus, deliveryMethod);
        
        if (mappedStatusKey) {
          const dropdownData = response.status_list[mappedStatusKey] || [];
          const formattedDropdownData: DropdownOption[] = dropdownData.map(
            (status: string) => ({
              label: status,
              value: status,
            })
          );
          setStatusList(formattedDropdownData);
          return formattedDropdownData;
        }
      }
      setStatusList([]);
      return [];
    } catch (error) {
      console.error("Error fetching order status list:", error);
      setStatusList([]);
      return [];
    }
  }, []);

  const fetchCancellationReasons = useCallback(async () => {
    try {
      const response = await fetchSellerCancellationReasons();
      if (response?.cancel_reason_list) {
        setReasonOptions(response.cancel_reason_list);
        return response.cancel_reason_list;
      }
      setReasonOptions([]);
      return [];
    } catch (error) {
      console.error("Error fetching cancellation reasons:", error);
      setReasonOptions([]);
      return [];
    }
  }, []);

  return { 
    statusList, 
    reasonOptions, 
    fetchStatusList, 
    fetchCancellationReasons,
    setReasonOptions 
  };
};

export const useOrderUpdate = () => {
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  const updateOrder = useCallback(async (
    orderId: string,
    productIds: string,
    orderStatus: string,
    cancelReason?: string,
    otherReason?: string
  ) => {
    try {
      setUpdateLoading(true);
      await sellerCancelLineItem(orderId, productIds, orderStatus, cancelReason, otherReason);
      return true;
    } catch (error) {
      console.error("Error updating order:", error);
      throw error;
    } finally {
      setUpdateLoading(false);
    }
  }, []);

  return { updateLoading, updateOrder };
};

// Helper function to map order status
const mapOrderStatus = (status: string, deliveryMethod: string): string => {
  switch (status.toUpperCase()) {
    case "NEW":
      return "initial_status";
    case "PENDING":
      return "";
    case "PROCESSING":
    case "PARTIALLY_PROCESSING":
      if (deliveryMethod === "DELIVERY") {
        return "processing_status_shipping";
      } else if (deliveryMethod === "PICKED_UP") {
        return "processing_status_pickup";
      }
      return "";
    case "COMPLETE":
    case "DELIVERED":
      return "";
    case "CANCELLED":
      return "";
    case "READY_TO_PICK":
    case "READY TO PICK":
      return "ready_to_picked";
    case "READY_TO_SHIP":
    case "READY TO SHIP":
      return "ready_to_shipped";
    case "OUT_FOR_DELIVERY":
    case "OUT FOR DELIVERY":
      return "out_for_delivery";
    case "SHIPPED":
      return "shipped_status";
    case "RETURN":
    case "REFUNDED":
      return "";
    default:
      return "";
  }
};